import { Flex, GapSize } from "@/components/atoms/flex";
import {
	ConversationSummaryKey,
	getCallSummary,
	getEmailSummary,
	getWebchatSummary,
	getWhatsAppSummary,
} from "@/repository/queryFns";
import {
	Button,
	Divider,
	Skeleton,
	SkeletonItem,
} from "@fluentui/react-components";
import { useQuery } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import { useStyle } from "./style";
import { MessagingConversationSummaryPopoverProps } from "./types";
import { DocumentOnePageSparkle20Regular } from "@fluentui/react-icons";
import DOMPurify from "dompurify";
import { InteractionType } from "@/common/types";
import { useMemo } from "react";
import { PanelHeader } from "@/components/atoms/panelHeader";
import { Close32Regular } from "@/common/icons/closeIcon/Close32Regular";
import { AiIndicator } from "@/components/atoms/aiIndicator";
import { useIsSummaryGenerating } from "@/common/stores/nonpersistingStores/summaryGeneration/summaryGenerationStore";

export const MessagingConversationSummaryPopover = ({
	closePopover,
	isOpen,
	interactionId,
	interactionType,
	interactionStartDate,
}: MessagingConversationSummaryPopoverProps) => {
	const intl = useIntl();

	const classes = useStyle();

	// The close-time summary is generated in the background; show "generating" until it arrives.
	const isGenerating = useIsSummaryGenerating(interactionId);

	const getSummaryFn = useMemo(() => {
		switch (interactionType) {
			case InteractionType.Email:
				return getEmailSummary;
			case InteractionType.WhatsApp:
				return getWhatsAppSummary;
			case InteractionType.Webchat:
				return getWebchatSummary;
			case InteractionType.Call:
				return getCallSummary;
			default:
				return () => Promise.resolve("");
		}
	}, []);

	const {
		data: summary,
		isLoading,
		isFetching,
	} = useQuery<string>({
		queryKey: [ConversationSummaryKey, interactionId],
		queryFn: (_: any) => {
			if (!interactionId) {
				return Promise.resolve("");
			}
			return getSummaryFn(interactionId, interactionStartDate);
		},
		enabled: isOpen && !!interactionId,
		refetchOnMount: "always",
	});

	return (
		<Flex column className={classes.container}>
			<PanelHeader
				left={
					<Flex hAlign="flex-start" column={false} vAlign="center">
						<DocumentOnePageSparkle20Regular />
						<Flex gap={GapSize.Small} vAlign="center">
							<p className={classes.title}>
								{intl.formatMessage({
									id: `Summary.Title`,
								})}
							</p>
							<AiIndicator mode="icon-only" />
						</Flex>
					</Flex>
				}
				right={
					<Button
						appearance="transparent"
						shape="circular"
						icon={<Close32Regular />}
						size="small"
						onClick={closePopover}
					/>
				}
				className={classes.panelHeader}
			/>
			<Divider />
			{isLoading || isFetching ? (
				<Skeleton aria-label="Loading Content">
					<Flex
						gap={GapSize.Small}
						className={classes.skeletonContainer}
						column
					>
						<SkeletonItem size={24} />
						<SkeletonItem size={24} />
						<SkeletonItem size={24} />
					</Flex>
				</Skeleton>
			) : (
				<Flex hAlign="flex-start" className={classes.summaryContainer}>
					{!summary && isGenerating && (
						<span>{intl.formatMessage({ id: "Summary.Generating" })}</span>
					)}
					{!summary && !isGenerating && (
						<span>{intl.formatMessage({ id: "Summary.NoData" })}</span>
					)}
					{summary && (
						<div
							dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(summary!) }}
							style={{ width: "300px" }}
						></div>
					)}
				</Flex>
			)}
		</Flex>
	);
};
